import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { BRANCHES, IMAGES, SERVICES, formatPrice } from "@/data/salon";
import { useBooking } from "@/context/BookingContext";

const FEATURED = [
  {
    serviceId: "hair-springburn-keratin",
    image: IMAGES.hairHero,
    title: "Glass-smooth keratin",
    copy: "A polished smoothing ritual for softer movement, mirror shine and easier styling.",
  },
  {
    serviceId: "hair-maryhill-wash-cut-blowdry",
    image: IMAGES.hairCut,
    title: "Precision cut & finish",
    copy: "Considered shaping, cleansing and a finished blow dry at our Maryhill studio.",
  },
  {
    serviceId: "hair-maryhill-full-highlights-cut-blowdry",
    image: IMAGES.hairBlowdry,
    title: "Dimensional highlights",
    copy: "Light, depth and a professional finish tailored to your length and style.",
  },
];

export const HairShowcase = () => {
  const { openBooking } = useBooking();

  return (
    <section id="hair-showcase" data-testid="hair-showcase-section" className="relative overflow-hidden bg-ink py-24 text-cream sm:py-32">
      <div className="pointer-events-none absolute right-4 top-10 select-none font-serif text-[8rem] italic leading-none text-cream/[0.035] sm:text-[12rem]">Hair</div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-pinklight">Now at our Glasgow studios</span>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
              Hair, styled with intention.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="max-w-sm text-sm font-light leading-relaxed text-cream/70 sm:text-base">
              Explore dedicated Treatwell-matched hair menus at Springburn and Maryhill — from precision cuts to colour, keratin and occasion styling.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="hair-showcase-branch-pills">
              {BRANCHES.filter((b) => b.id !== "motherwell").map((branch) => (
                <span key={branch.id} className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-cream/75">
                  <MapPin className="h-3 w-3 text-pinklight" /> {branch.name.replace("Glasgow · ", "")}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {FEATURED.map((feature, index) => {
            const service = SERVICES.find((item) => item.id === feature.serviceId);
            const branch = BRANCHES.find((item) => item.id === service?.branches?.[0]);
            if (!service || !branch) {
              console.warn(`Featured hair service is unavailable: ${feature.serviceId}`);
              return null;
            }
            return (
              <motion.article
                key={feature.serviceId}
                data-testid={`hair-showcase-card-${service.id}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: index * 0.12 }}
                className="group relative min-h-[420px] overflow-hidden rounded-[28px] border border-cream/10 bg-cream/5 lg:min-h-[500px]"
              >
                <img
                  src={feature.image}
                  alt={service.name}
                  loading="lazy"
                  data-testid={`hair-showcase-image-${service.id}`}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-ink/35 transition-colors duration-500 group-hover:bg-ink/25" />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink via-ink/55 to-transparent" />
                <div className="relative flex h-full flex-col justify-end p-6 sm:p-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-pinklight">{branch.name}</p>
                  <h3 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">{feature.title}</h3>
                  <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-cream/75">{feature.copy}</p>
                  <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                    <span className="font-mono text-xs uppercase tracking-[0.16em] text-cream/80">
                      {service.duration} · {service.from ? "from " : ""}£{formatPrice(service.price)}
                    </span>
                    <button
                      type="button"
                      data-testid={`hair-showcase-book-${service.id}`}
                      onClick={() => openBooking(service.id)}
                      className="group/button inline-flex items-center gap-2 rounded-full bg-cream px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink transition-colors hover:bg-pinklight"
                    >
                      Book {branch.name.replace("Glasgow · ", "")}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover/button:-translate-y-0.5 group-hover/button:translate-x-0.5" />
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
